/**
 * The marks a calendar event wears: what it IS, and how it is delivered.
 *
 * ── Provenance: transcribed from the MOBILE app's glyph registry, not drawn here ───────────────
 * `move-with-me-frontend/move-with-me/src/design-system/primitives/Icon.glyphs.tsx` is that app's
 * one icon set, and `docs/design/README.md` records it as the set this app is meant to ship too
 * ("placeholders are inline SVG, the shipping set is `Icon.glyphs` ported from mobile"). Every path
 * below is copied from it character for character, so the trainer meets one drawing per concept on
 * both platforms. That file carries no licence header of its own — its own provenance note names
 * KANON's `modules/auth-core.js` as the original — so there is nothing to reproduce here beyond
 * saying where each one came from.
 *
 * The construction is the same one `AuthIcons` and `RecordIcons` already use in this repo, and it is
 * also mobile's: a 24×24 box, `fill: none`, `currentColor` at a 2px round stroke. Mobile sets those
 * on the `<Svg>` wrapper and keeps the glyph children as pure geometry; the same split holds here.
 *
 * ── One of them is authored, and it is flagged as such ────────────────────────────────────────
 * Mobile has NO glyph for "bez planu" — it renders that fact as italic text beside a 3px dot. So
 * `NoPlanIcon` is this repo's own, and it is the smallest departure available: mobile's own `plan`
 * document, minus its two body rules, with a stroke through it. The negation is not invented either
 * — `AuthIcons.EyeOffIcon` is the same eye with `M4 4l16 16` across it, and mobile's `calx` negates
 * its calendar the same way.
 *
 * ── Every one of them is `aria-hidden`, always ────────────────────────────────────────────────
 * A glyph on this surface is never the only channel: the word it stands for is either visible beside
 * it, or in the block's accessible name, or in the `title` its wrapper carries. `model/clientEvent`
 * is where that pairing is decided, so a caller cannot draw one of these with nothing to read.
 */

interface IconProps {
  className?: string;
}

const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * Stacjonarnie · z trenerem. Mobile's `trainer` — the crossed-barbell mark its own clients calendar
 * puts on a with-trainer session (`CalendarSessionCard`'s `MODE_GLYPH.withTrainer`).
 */
export function WithTrainerIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M6.5 6.5 17.5 17.5" />
      <path d="m18 22 4-4" />
      <path d="m2 6 4-4" />
      <path d="m3 10 7-7" />
      <path d="m14 21 7-7" />
    </svg>
  );
}

/**
 * Zdalnie · samodzielnie. Mobile's `solo` — the one it puts on a session with nobody coaching it
 * (`MODE_GLYPH.solo`). One person, which is the whole of what this axis says.
 */
export function SoloIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="7.5" r="3.2" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </svg>
  );
}

/**
 * Konsultacja. Mobile's `video`, which is the glyph its calendar's create wizard puts against the
 * Consultation session type (`WhatStep`). Mobile's clients-calendar card draws no consultation mark
 * at all — it does not distinguish one — so this is borrowed one surface over rather than authored.
 */
export function ConsultationIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
      <path d="m15.5 10 6-3.5v11L15.5 14z" />
    </svg>
  );
}

/** Z planu. Mobile's `plan` — a document with a calendar tab at each corner and two body rules. */
export function FromPlanIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="4" y="3" width="16" height="18" rx="2.5" />
      <path d="M8 3v3M16 3v3M8 11h8M8 15h5" />
    </svg>
  );
}

/**
 * Bez planu. **Authored** — see the header. Mobile's `plan` with its body rules dropped and a stroke
 * across it, which is this repo's own negation idiom (`AuthIcons.EyeOffIcon`) and mobile's (`calx`).
 * The rules come off rather than staying under the stroke: at the 11px this is drawn at, a document
 * carrying two rules AND a diagonal is a smudge.
 */
export function NoPlanIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="4" y="3" width="16" height="18" rx="2.5" />
      <path d="M8 3v3M16 3v3" />
      <path d="M6.5 19 17.5 6" />
    </svg>
  );
}

/**
 * Rezerwacja — a booked time with nothing in it yet (`calendar-events` R18). Mobile's `cal`, the
 * plain calendar: the trainer reserved the DATE and that is all this row is so far.
 */
export function ReservationIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <path d="M8 2v4M16 2v4M3 10h18" />
    </svg>
  );
}
